<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { computed, ref, onMounted } from "vue";
import ModalFormTemplate from "../../../../components/ui/Templates/ModalFormTemplate.vue";
import DateInput from "../../../../components/ui/form/DateInput.vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";
import {
  formatNumber,
  toTitle,
} from "../../../../utils/CommonFunctions";
import MessageBox from "../../../../services/MessageBox";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  lists: any;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  amount: props.data?.amount || 0,
  chart_of_accounts_id: props.data?.chart_of_accounts_id || "",
  currencys_id: props.data?.currencys_id || 1,
  date: props.data?.date || new Date().toISOString().split("T")[0],
  exchange_rate: props.data?.exchange_rate || 1,
  id: props.data?.id || null,
  ledger_group_id: props.data?.ledger_group_id || "",
  ledgers: props.data?.ledgers || [],
  record_id: props.data?.record_id || null,
  record_type: props.data?.record_type || "",
  reference: props.data?.reference || "",
  remarks: props.data?.remarks || "",
  transaction_type: props.data?.transaction_type || "",
  uid: props.data?.uid || "",
});

setFieldRules("date", [required("Date is required")]);
setFieldRules("currencys_id", [required("Currency is required")]);
setFieldRules("exchange_rate", [required("Exchange Rate is required")]);

const validateGrid = () => {
  let isValid = false;
  fields.value.ledgers.value.forEach((item: any) => {
    if (item.chart_of_accounts_id != "" && (item.dr > 0 || item.cr > 0)) {
      isValid = true;
      return;
    } 
  });
  return isValid;
};
const handleSubmit = async (close: boolean, newRecord: boolean) => {
  console.log('newRecord', newRecord)
  if (validateForm()) {
    if (!validateGrid()) {
      MessageBox.show("Select the Ledgers to save", "warning");
      return;
    }
    if (debitTotal.value != creditTotal.value) {
      MessageBox.show("Debit and Credit should be equal", "warning");
      return;
    }
    const action = formData.value.uid != "" ? "update" : "create";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + action,
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      console.log('isReadonly', isReadOnly.value);
      Toast.show("saved successfully");
      emit("save", response.data);
      if (close) {
        emit("close");
      } else if (newRecord) {
        console.log('newRecord111', newRecord);
        resetForm();
      } else {
        setFormData(response.data);
      }
    }
  }
};

const debitTotal = computed(() => {
  return fields.value.ledgers.value.reduce((acc: any, item: any) => {
    return acc + parseFloat(item.dr);
  }, 0);
});

const creditTotal = computed(() => {
  return fields.value.ledgers.value.reduce((acc: any, item: any) => {
    return acc + parseFloat(item.cr);
  }, 0);
});

const getRecord = async () => {
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    {
      uid: props.data.uid,
    }
  );
  if (response.success == true) {
    setFormData(response.data);
  }
};

const currencys = ref([]);
const chart_of_accounts = ref([]);

const setLists = async (data: any) => {
  if (!data) return;
  currencys.value = data["currencys"];
  chart_of_accounts.value = data["chart_of_accounts"];
};

const convertedRecordType = computed({
  get: () => toTitle(fields.value.record_type.value),
  set: (value: string) => {
    fields.value.record_type.value = value;
  },
});

const addRowToLedgers = () => {
  fields.value.ledgers.value.push({
    amount: 0,
    chart_of_account: "",
    cr: 0,
    currencys_id: fields.value.currencys_id.value,
    currency: "",
    dr: 0,
    exchange_rate: fields.value.exchange_rate.value,
    ledger_group_id: "",
    record_id: fields.value.record_id.value,
    record_type: fields.value.record_type.value,
    reference: fields.value.reference.value,
    remarks: fields.value.remarks.value,
    uid: "",
  });
};

const isReadOnly = ref(true);
onMounted(() => {
  isReadOnly.value = props.mode == 'view'
  setLists(props.lists)
  if (props.mode == 'create') {
      resetForm()
      fields.value.ledgers.value = [];
      addRowToLedgers();
      addRowToLedgers();
    } else if (props.data) {
      getRecord()
    }
})


</script>
<template>
  <ModalFormTemplate
    :data="data"
    :mode="mode"
    :title="title"
    :endPoint="endPoint"
    :perm="permissions"
    :listCreateName="listCreateName"
    :tabList="[]"
    :formData="formData"
    @save="handleSubmit"
    @close="$emit('close')"
    width="60rem"
  >
    <template #form>
      <div class="grid grid-cols-5 gap-6">
        <DateInput
          v-model="fields.date.value"
          label="Date"
          :readonly="isReadOnly"
          :error="fields.date.touched ? fields.date.error : null"
          @blur="fields.date.touched = true"
        />

        <SearchSelect
          label="Currency"
          v-model="fields.currencys_id.value"
          class="col-span-2"
          :readonly="isReadOnly"
          :error="
            fields.currencys_id.touched ? fields.currencys_id.error : null
          "
          @blur="fields.currencys_id.touched = true"
          :options="currencys"
        />

        <NumberInput
          v-model="fields.exchange_rate.value"
          label="Exchange Rate"
          :readonly="isReadOnly"
          :error="
            fields.exchange_rate.touched ? fields.exchange_rate.error : null
          "
          @blur="fields.exchange_rate.touched = true"
        />

        <TextInput
          v-model="convertedRecordType"
          label="Record Type"
          v-if="mode != 'create'"
          :readonly="true"
          :error="fields.record_type.touched ? fields.record_type.error : null"
          @blur="fields.record_type.touched = true"
        />
      </div>

      <div class="overflow-x-auto overflow-y-auto min-h-[400px]">
        <table class="w-full">
          <thead class="bg-white dark:bg-gray-900">
            <tr>
              <!-- Fixed left column -->
              <th
                class="sticky left-0 z-20 px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Account
              </th>

              <!-- Scrollable columns -->
              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Photo</th>-->
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Debit
              </th>
              <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
              <th
                class="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Credit
              </th>
              <th
                class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
              >
                Reference
              </th>
              <!--<th class="sticky right-0 z-20 px-4 py-3"></th>-->
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <template
              v-for="(item) in fields.ledgers.value"
              :key="item.id"
            >
              <tr>
                <!-- Fixed left column -->
                <td class="px-4 py-3 min-w-60">
                  <SearchSelect
                    v-if="!isReadOnly"
                    v-model="item.chart_of_accounts_id"
                    placeholder="Select Account"
                    :options="chart_of_accounts"
                    :readonly="isReadOnly"
                  />
                  <span v-else>{{ item.chart_of_account }}</span>
                </td>
                <td
                  class="px-4 py-3 relative group min-w-40 align-middle text-right"
                >
                  <NumberInput
                    v-model="item.dr"
                    :readonly="isReadOnly"
                    :showControls="false"
                    v-if="!isReadOnly"
                  />
                  <span v-else>{{ formatNumber(item.dr) }}</span>
                </td>
                <td
                  class="px-4 py-3 relative group min-w-40 align-middle text-right"
                >
                  <NumberInput
                    v-model="item.cr"
                    :readonly="isReadOnly"
                    :showControls="false"
                    v-if="!isReadOnly"
                  />
                  <span v-else>{{ formatNumber(item.cr) }}</span>
                </td>
                <td class="px-4 py-3 min-w-40">
                  <TextInput
                    label=""
                    v-model="item.reference"
                    :readonly="isReadOnly"
                    v-if="!isReadOnly"
                  />
                  <span v-else>{{ item.reference }}</span>
                </td>
                <!--<td class="sticky right-0 z-10 bg-white dark:bg-gray-900 px-4 py-3">
                      <button
                        @click="removeItem(index)"
                        class="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        ✕
                      </button>
                    </td>-->
              </tr>
            </template>
          </tbody>
          <tfoot class="bg-white dark:bg-gray-900">
            <tr class="border-t border-gray-200 dark:border-gray-700 border-b">
              <!-- Fixed left column -->
              <td class="px-4 py-3 min-w-60">Total</td>
              <td
                class="px-4 py-3 relative group min-w-40 align-middle text-right"
              >
                <span>{{ formatNumber(debitTotal) }}</span>
              </td>
              <td
                class="px-4 py-3 relative group min-w-40 align-middle text-right"
              >
                <span>{{ formatNumber(creditTotal) }}</span>
              </td>
              <td class="px-4 py-3 min-w-40"></td>
              <!--<td class="sticky right-0 z-10 bg-white dark:bg-gray-900 px-4 py-3">
                      <button
                        @click="removeItem(index)"
                        class="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        ✕
                      </button>
                    </td>-->
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="grid grid-cols-2 gap-6">
        <TextInput
          v-model="fields.reference.value"
          label="Reference"
          :readonly="isReadOnly"
        />
        <TextInput
          v-model="fields.remarks.value"
          label="Remarks"
          :readonly="isReadOnly"
        />
      </div>
    </template>
  </ModalFormTemplate>
</template>
